/**
 * User Management Page (Sprint 5 S5-B-pages; GH #2307 refinement)
 *
 * Tenant-scoped FI Staff roster page -- the FI admin's control surface over
 * who holds access inside their institution. Lists staff via the canonical
 * `useStaff` hook (FI Staff Lifecycle spec) and routes row clicks to the
 * staff detail page at `/admin/staff/:staffId`. The header [Invite Staff]
 * button opens the `InviteStaffModal` and renders the post-success notice.
 *
 * Sprint 1 schema deviation #1 honored downstream: invite is single-role.
 * Multi-role grants happen post-acceptance from the staff detail page.
 *
 * GH #2307 deliverable 2: outstanding invitations render in
 * `PendingInvitationsCard`, which consumes the previously unconsumed
 * `useInvitations` / `useResendInvitation` / `useRevokeInvitation` hooks.
 *
 * GH #2307 deliverable 3: the roster honors the `status`, `page`, and
 * `pageSize` parameters `useStaff` already accepts and the backend route
 * already implements (staff-lifecycle.routes.ts:404-449 -- status allow-list,
 * page defaulting to 1, pageSize clamped to 200). Before this change the page
 * called `useStaff()` bare, so an institution with more staff than one page
 * silently saw page 1 only with no indication that rows were withheld.
 *
 * Composes `@aaimplatform/ui` primitives (Heading, BodyText, Button, Card,
 * Table, Badge, Alert, Select, EmptyState) with zero local
 * re-implementation per the @aaimplatform/ui mandate.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus as InviteIcon, Users as UsersIcon, UserX as NoStaffIcon } from 'lucide-react';
import {
  Alert,
  Badge,
  BodyText,
  Button,
  Card,
  EmptyState,
  Heading,
  Select,
  Table,
  type BadgeProps,
  type SelectOption,
  type TableColumn,
} from '@aaimplatform/ui';
import {
  useStaff,
  type Staff,
  type RoleRef,
  type TenantUserStatus,
} from '../../hooks/useStaffLifecycle';
import { InviteStaffModal } from './InviteStaff';
import { PendingInvitationsCard } from '../../components/staff/PendingInvitationsCard';

const STATUS_TO_BADGE: Record<
  TenantUserStatus,
  { variant: NonNullable<BadgeProps['variant']>; label: string }
> = {
  ACTIVE: { variant: 'success', label: 'Active' },
  INVITED: { variant: 'warning', label: 'Invited' },
  SUSPENDED: { variant: 'warning', label: 'Suspended' },
  OFFBOARDED: { variant: 'neutral', label: 'Off-boarded' },
  DELETED: { variant: 'error', label: 'Deleted' },
};

/**
 * Roster status filter. Values other than ALL are the canonical
 * `TenantUserStatus` enum members, so the option list cannot drift from the
 * generated type: the compiler rejects a value that is not in the enum.
 */
export const STAFF_STATUS_FILTER_OPTIONS: SelectOption[] = [
  { value: 'ALL', label: 'All statuses' },
  ...(
    ['ACTIVE', 'INVITED', 'SUSPENDED', 'OFFBOARDED'] as const satisfies readonly TenantUserStatus[]
  ).map((status) => ({ value: status, label: STATUS_TO_BADGE[status].label })),
];

/** Matches the backend default at staff-lifecycle.routes.ts:409 (clamped to 200). */
export const STAFF_PAGE_SIZE = 25;

function formatRoleName(role: RoleRef): string {
  return role.name || role.slug;
}

function pickPrimaryRole(roles: RoleRef[]): RoleRef | null {
  if (!roles || roles.length === 0) return null;
  // Prefer fi_admin, then fi_compliance, then fi_loan_officer, otherwise first.
  const priority = ['fi_admin', 'fi_compliance', 'fi_loan_officer'];
  for (const slug of priority) {
    const match = roles.find((r) => r.slug === slug);
    if (match) return match;
  }
  return roles[0] ?? null;
}

function formatRelativeOrDate(value?: string | null): string {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

export function UserManagementPage() {
  const navigate = useNavigate();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteNotice, setInviteNotice] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<TenantUserStatus | 'ALL'>('ALL');
  const [page, setPage] = useState(1);

  const staffQuery = useStaff({
    status: statusFilter === 'ALL' ? undefined : statusFilter,
    page,
    pageSize: STAFF_PAGE_SIZE,
  });

  const staff = staffQuery.data?.items ?? [];
  const total = staffQuery.data?.total ?? 0;
  const pageSize = staffQuery.data?.pageSize ?? STAFF_PAGE_SIZE;
  const currentPage = staffQuery.data?.page ?? page;
  const pageCount = total > 0 ? Math.ceil(total / pageSize) : 1;
  const firstOnPage = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const lastOnPage = total === 0 ? 0 : firstOnPage + staff.length - 1;
  const hasPrev = currentPage > 1;
  const hasNext = currentPage < pageCount;

  const columns: TableColumn<Staff>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Name',
        render: (_value, row) => (
          <div className="min-w-0">
            <div className="text-sm font-sans font-medium text-primary truncate">
              {row.displayName || row.email}
            </div>
            {row.displayName && (
              <div className="text-xs font-sans text-tertiary truncate">
                {row.email}
              </div>
            )}
          </div>
        ),
      },
      {
        key: 'email',
        label: 'Email',
        render: (_value, row) => (
          <span className="text-sm font-mono text-secondary">{row.email}</span>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        render: (_value, row) => {
          const badge = STATUS_TO_BADGE[row.status] ?? STATUS_TO_BADGE.INVITED;
          return (
            <Badge variant={badge.variant} size="small">
              {badge.label}
            </Badge>
          );
        },
      },
      {
        key: 'primaryRole',
        label: 'Primary role',
        render: (_value, row) => {
          const primary = pickPrimaryRole(row.roles ?? []);
          if (!primary) {
            return (
              <span className="text-xs font-sans text-tertiary">No roles</span>
            );
          }
          const remaining = (row.roles?.length ?? 0) - 1;
          return (
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-sans text-primary">
                {formatRoleName(primary)}
              </span>
              {remaining > 0 && (
                <span className="text-xs font-mono text-tertiary">
                  +{remaining}
                </span>
              )}
            </div>
          );
        },
      },
      {
        key: 'lastLoginAt',
        label: 'Last activity',
        render: (_value, row) => (
          <span className="text-sm font-sans text-secondary">
            {formatRelativeOrDate(row.lastLoginAt)}
          </span>
        ),
      },
    ],
    [],
  );

  const handleRowClick = (row: Staff) => {
    void navigate(`/admin/staff/${row.id}`);
  };

  const handleInvited = (email: string) => {
    setInviteNotice(`Invitation sent to ${email}.`);
    void staffQuery.refetch();
  };

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          {/* Page title lives in the sticky topnav (AppHeader); the body opens
              with the descriptive subtitle, no redundant H1. */}
          <BodyText size="sm" color="tertiary">
            Manage staff access for your institution. Invitations are
            specific to your institution. An invitee becomes an Active staff
            member on accepting the emailed invitation; depending on how your
            staff sign in, they set a password or sign in through your
            institution&apos;s identity provider.
          </BodyText>
        </div>
        <Button
          variant="primary"
          onClick={() => {
            setInviteNotice(null);
            setInviteOpen(true);
          }}
        >
          <InviteIcon size={16} className="mr-2" />
          Invite staff
        </Button>
      </div>

      {inviteNotice && (
        <div className="mb-4 relative">
          <Alert type="success" title="Invitation sent">
            {inviteNotice}
          </Alert>
          <button
            type="button"
            onClick={() => setInviteNotice(null)}
            aria-label="Dismiss notice"
            className="absolute top-3 right-3 text-tertiary hover:text-primary text-sm font-mono"
          >
            x
          </button>
        </div>
      )}

      {staffQuery.isError && (
        <div className="mb-4">
          <Alert type="error" title="Unable to load staff">
            {staffQuery.error?.message ?? 'Staff list could not be loaded.'}
          </Alert>
        </div>
      )}

      <Card>
        <div className="px-6 py-4 border-b border-recessed flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <UsersIcon size={18} className="text-accent" aria-hidden />
            <Heading level={2} size="subsection">
              Staff members
            </Heading>
            <span className="ml-2 text-xs font-mono text-tertiary">
              {staffQuery.isFetching ? '...' : total}
            </span>
          </div>
          <div className="sm:w-56">
            <Select
              id="staff-status-filter"
              label="Status"
              options={STAFF_STATUS_FILTER_OPTIONS}
              value={statusFilter}
              onChange={(event) => {
                const next = event.target.value as TenantUserStatus | 'ALL';
                setStatusFilter(next);
                // A narrower filter shortens the result set; page 2 of the
                // prior filter is meaningless against the new one.
                setPage(1);
              }}
            />
          </div>
        </div>
        <div className="p-4">
          {staffQuery.isLoading ? (
            <div className="flex justify-center items-center h-48">
              <div
                role="status"
                aria-label="Loading staff"
                className="w-10 h-10 border-4 border-accent border-t-transparent rounded-full animate-spin"
              />
            </div>
          ) : staff.length === 0 ? (
            <EmptyState
              icon={<NoStaffIcon className="h-8 w-8" aria-hidden />}
              title={
                statusFilter === 'ALL'
                  ? 'No staff members yet'
                  : 'No staff members match this status'
              }
              description={
                statusFilter === 'ALL'
                  ? 'Use [Invite staff] to send the first invitation. Outstanding invitations are listed below; a staff member appears here once they accept.'
                  : 'Your institution has staff records, but none in the selected status. Switch back to All statuses to see the full roster.'
              }
              action={
                statusFilter === 'ALL' ? undefined : (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setStatusFilter('ALL');
                      setPage(1);
                    }}
                  >
                    Show all statuses
                  </Button>
                )
              }
            />
          ) : (
            <>
              <Table
                columns={columns}
                data={staff}
                onRowClick={handleRowClick}
                hoverable
              />
              {/* Total is always stated so page-1 truncation is visible; the
                  prior version rendered only the loaded rows with no total. */}
              <div className="mt-4 flex flex-col gap-3 px-2 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-xs font-sans text-tertiary">
                  Showing {firstOnPage}-{lastOnPage} of {total} staff
                  {pageCount > 1 && ` (page ${currentPage} of ${pageCount})`}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="tertiary"
                    size="sm"
                    disabled={!hasPrev || staffQuery.isFetching}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="tertiary"
                    size="sm"
                    disabled={!hasNext || staffQuery.isFetching}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </Card>

      <div className="mt-6">
        <PendingInvitationsCard
          onInvitationChanged={() => {
            // Neither action changes a roster row -- an outstanding invitation
            // has no TenantUser until it is accepted -- but refetching keeps
            // the two surfaces consistent if an accept lands between renders.
            void staffQuery.refetch();
          }}
        />
      </div>

      <InviteStaffModal
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onInvited={handleInvited}
      />
    </div>
  );
}

export default UserManagementPage;
